import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Copy, Check } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useInstallPrompt, type IOSBrowser } from "@/hooks/useInstallPrompt";

const IOS_INSTALL_TRIGGER_KEY = "br_ios_install_trigger";
const IOS_GROUP_VISIT_KEY = "br_ios_group_visits";

/** SVG share icon matching iOS share button (arrow-up from box) */
function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

/** Plus icon matching iOS "Add to Home Screen" action */
function PlusBoxIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

/** Checkmark icon */
function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="9 12 11.5 14.5 16 9.5" />
    </svg>
  );
}

function getShareLocationText(browser: IOSBrowser): string {
  switch (browser) {
    case "safari":
      return "בתחתית המסך";
    case "chrome":
    case "firefox":
    case "edge":
      return "בסרגל העליון";
    default:
      return "";
  }
}

function IOSInstallModal({
  iosBrowser,
  onDismiss,
}: {
  iosBrowser: IOSBrowser;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const isKnownBrowser = iosBrowser !== "other";

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement("input");
      input.value = window.location.origin;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <motion.div
      key="ios-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[60] bg-black/50"
      onClick={onDismiss}
    >
      <motion.div
        key="ios-sheet"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="absolute bottom-0 left-0 right-0 bg-card rounded-t-3xl border-t border-border shadow-2xl max-h-[85vh] overflow-y-auto"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        <div className="px-6 pb-8 pt-4">
          {/* App icon + title */}
          <div className="flex flex-col items-center text-center mb-6">
            <img
              src="/apple-touch-icon.png"
              alt="BRACKET RUN"
              className="w-16 h-16 rounded-2xl shadow-lg mb-4"
            />
            <h2 className="text-xl font-bold tracking-tight">
              התקינו את Bracket Run
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              גישה מהירה ישירות מהמסך
            </p>
          </div>

          {isKnownBrowser ? (
            /* Steps for Safari / Chrome / Firefox / Edge */
            <div className="space-y-3 mb-6">
              {/* Step 1 — Share */}
              <div className="flex items-start gap-4 bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/15 text-accent font-bold text-sm flex-shrink-0 mt-0.5">
                  1
                </div>
                <div className="flex items-start gap-3 flex-1">
                  <ShareIcon className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      לחצו על כפתור השיתוף
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {getShareLocationText(iosBrowser)}
                    </p>
                  </div>
                </div>
                {/* Arrow indicator */}
                {iosBrowser === "safari" ? (
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-[4.5rem] pointer-events-none hidden" />
                ) : null}
              </div>

              {/* Step 2 — Add to Home Screen */}
              <div className="flex items-start gap-4 bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/15 text-accent font-bold text-sm flex-shrink-0 mt-0.5">
                  2
                </div>
                <div className="flex items-start gap-3 flex-1">
                  <PlusBoxIcon className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-foreground">
                    בחרו &lsquo;הוסף למסך הבית&rsquo;
                  </p>
                </div>
              </div>

              {/* Step 3 — Confirm */}
              <div className="flex items-start gap-4 bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/15 text-accent font-bold text-sm flex-shrink-0 mt-0.5">
                  3
                </div>
                <div className="flex items-start gap-3 flex-1">
                  <CheckCircleIcon className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-foreground">
                    לחצו &lsquo;הוסף&rsquo; ויאללה!
                  </p>
                </div>
              </div>

              {/* Directional hint */}
              <div className="text-center pt-2">
                <p className="text-xs text-muted-foreground/60">
                  {iosBrowser === "safari"
                    ? "👇 כפתור השיתוף נמצא למטה בסרגל הניווט"
                    : "👆 כפתור השיתוף נמצא למעלה בסרגל הכלים"}
                </p>
              </div>
            </div>
          ) : (
            /* Unknown browser — suggest Safari */
            <div className="mb-6">
              <div className="bg-muted/30 rounded-xl p-5 text-center space-y-4">
                <p className="text-sm text-foreground font-medium">
                  לחוויה הטובה ביותר, פתחו ב-Safari
                </p>
                <p className="text-xs text-muted-foreground">
                  העתיקו את הקישור ופתחו אותו ב-Safari כדי להתקין את האפליקציה
                </p>
                <button
                  onClick={handleCopyUrl}
                  className="flex items-center justify-center gap-2 mx-auto bg-accent text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-accent/90 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>הקישור הועתק!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>העתקת קישור</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Dismiss button */}
          <button
            onClick={onDismiss}
            className="w-full py-3 rounded-xl bg-muted/40 text-sm font-medium text-muted-foreground hover:bg-muted/60 transition-colors"
          >
            הבנתי
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/** Gentle reminder banner for iOS (after 3+ sessions without install) */
function IOSGentleReminder({
  onOpen,
  onDismiss,
}: {
  onOpen: () => void;
  onDismiss: () => void;
}) {
  return (
    <motion.div
      key="ios-reminder"
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg"
      dir="rtl"
    >
      <div className="flex items-center justify-between px-4 py-3 gap-3">
        <button
          onClick={onOpen}
          className="flex items-center gap-3 flex-1 min-w-0"
        >
          <img
            src="/apple-touch-icon.png"
            alt="BRACKET RUN"
            className="w-8 h-8 rounded-lg flex-shrink-0"
          />
          <p className="text-sm text-foreground leading-snug">
            להתקנה לחצו כאן
          </p>
        </button>
        <button
          onClick={onDismiss}
          className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted flex-shrink-0"
          aria-label="סגור"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

/** Android install banner — UNCHANGED from original */
function AndroidBanner({
  promptInstall,
  dismiss,
}: {
  promptInstall: () => Promise<void>;
  dismiss: () => void;
}) {
  return (
    <motion.div
      key="a2hs-banner"
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg"
      dir="rtl"
    >
      <div className="flex items-center justify-between px-4 py-3 gap-3">
        {/* Icon + text */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <img
            src="/apple-touch-icon.png"
            alt="BRACKET RUN"
            className="w-8 h-8 rounded-lg flex-shrink-0"
          />
          <div className="min-w-0">
            <p className="text-sm text-foreground leading-snug">
              הוסף את{" "}
              <span className="font-semibold">BRACKET</span>{" "}
              <span className="font-semibold text-gradient">RUN</span>{" "}
              למסך הבית
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={promptInstall}
            className="flex items-center gap-1.5 bg-accent text-white text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-accent/90 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            <span>התקן</span>
          </button>
          <button
            onClick={dismiss}
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted"
            aria-label="סגור"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function AddToHomeScreen() {
  const {
    showPrompt,
    isIOS,
    iosBrowser,
    isInstallable,
    isStandalone,
    showIOSModal,
    promptInstall,
    dismiss,
    openIOSModal,
  } = useInstallPrompt();

  const location = useLocation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if this is the gentle reminder state (iOS, 3+ sessions)
  const reminderCount = parseInt(
    localStorage.getItem("br_a2hs_reminder_count") || "0",
    10
  );
  const isGentleReminder = isIOS && reminderCount >= 3;

  // iOS auto-trigger logic — route-aware, does NOT affect Android
  useEffect(() => {
    if (!isIOS || isStandalone) return;

    // Check if permanently or session-dismissed
    const permanentDismissed =
      localStorage.getItem("br_a2hs_dismissed") === "true";
    const sessionDismissed =
      sessionStorage.getItem("br_a2hs_dismissed_session") === "true";
    if (permanentDismissed || sessionDismissed) return;

    const path = location.pathname;

    // Trigger 1: Post-onboarding or post-join flag (set by those flows)
    const trigger = localStorage.getItem(IOS_INSTALL_TRIGGER_KEY);
    if (trigger) {
      localStorage.removeItem(IOS_INSTALL_TRIGGER_KEY);
      timerRef.current = setTimeout(() => openIOSModal(), 2000);
      return;
    }

    // Trigger 2: 2nd visit to any group page
    if (/^\/group\/[^/]+$/.test(path)) {
      const currentCount = parseInt(
        localStorage.getItem(IOS_GROUP_VISIT_KEY) || "0",
        10
      );
      const newCount = currentCount + 1;
      localStorage.setItem(IOS_GROUP_VISIT_KEY, String(newCount));
      if (newCount === 2) {
        timerRef.current = setTimeout(() => openIOSModal(), 2000);
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isIOS, isStandalone, location.pathname, openIOSModal]);

  return (
    <AnimatePresence>
      {/* iOS modal */}
      {showIOSModal && (
        <IOSInstallModal iosBrowser={iosBrowser} onDismiss={dismiss} />
      )}

      {/* iOS gentle reminder banner (after 3+ sessions) */}
      {showPrompt && isIOS && isGentleReminder && !showIOSModal && (
        <IOSGentleReminder onOpen={openIOSModal} onDismiss={dismiss} />
      )}

      {/* Android banner — completely unchanged logic */}
      {showPrompt && !isIOS && isInstallable && (
        <AndroidBanner promptInstall={promptInstall} dismiss={dismiss} />
      )}
    </AnimatePresence>
  );
}
